// src/components/features/Interior/components/InteriorStep3UnitDescription.jsx
// Step 3: Room selection + popup with CARD-based question design
// Each question appears as its own labeled card — not a plain list dump
// UPDATED: Multiple same-room support — bedroom×2, washroom×3 etc.
//
// KEY CHANGE — room instance ID format:
//   First instance  → "bedroom"          (backward-compatible)
//   Second instance → "bedroom_2"
//   Third instance  → "bedroom_3"
//   ... and so on
//
// interiorRoomData key = "Ground Floor_bedroom", "Ground Floor_bedroom_2" etc.
// floorRooms value     = ["kitchen","bedroom","bedroom_2","washroom"] — array of instance IDs

import {
  Box, Flex, Text, Button, VStack, SimpleGrid, Icon,
  Badge, IconButton, Modal, ModalOverlay, ModalContent,
  ModalBody, ModalCloseButton, useDisclosure,
} from "@chakra-ui/react";
import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { FiMaximize2, FiX } from "react-icons/fi";
import { ROOM_TYPES, ROOM_QUESTIONS } from "../data/interiorRoomData";

// ─── Fullscreen zoom overlay ──────────────────────────────────────────────────
// Rendered via a portal straight into document.body so it always covers the
// real viewport (not just the space inside the modal / a transformed card).
function ZoomOverlay({ src, alt, onClose }) {
  return createPortal(
    <Box
      position="fixed" inset={0} zIndex={2000}
      bg="blackAlpha.900"
      display="flex" alignItems="center" justifyContent="center"
      onClick={onClose}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <IconButton
        aria-label="Close"
        icon={<Icon as={FiX} boxSize="20px" />}
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        position="absolute" top={4} right={4}
        borderRadius="full" bg="whiteAlpha.200" color="white"
        _hover={{ bg: "whiteAlpha.300" }}
      />
      <Box
        as="img"
        src={src}
        alt={alt}
        maxW="94vw"
        maxH="86vh"
        objectFit="contain"
        borderRadius="8px"
        onClick={(e) => e.stopPropagation()}
      />
    </Box>,
    document.body
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// "bedroom_2" → base roomId "bedroom"
const baseId = (instanceId) => instanceId.replace(/_\d+$/, "");

// How many instances of a roomId exist in the floor array
const countInstances = (arr, roomId) =>
  arr.filter((id) => baseId(id) === roomId).length;

// Build a new unique instance id for roomId
// First  → "bedroom", second → "bedroom_2", third → "bedroom_3"
const nextInstanceId = (arr, roomId) => {
  const n = countInstances(arr, roomId) + 1;
  return n === 1 ? roomId : `${roomId}_${n}`;
};

// Human-readable label for instance: "Bedroom", "Bedroom 2", "Bedroom 3"
const instanceLabel = (instanceId, baseLabel) => {
  const m = instanceId.match(/_(\d+)$/);
  return m ? `${baseLabel} ${m[1]}` : baseLabel;
};

// ─── Shared question card wrapper ─────────────────────────────────────────────
function QuestionCard({ index, total, question, children, answered }) {
  return (
    <Box
      border="1px solid"
      borderColor={answered ? "cyan.200" : "gray.100"}
      borderRadius="14px"
      bg="white"
      p={3.5}
    >
      <Flex align="center" gap={2} mb={3}>
        <Flex
          boxSize="22px" borderRadius="full" flexShrink={0}
          bg={answered ? "cyan.500" : "gray.200"}
          align="center" justify="center"
        >
          <Text fontSize="10px" fontWeight="700" color={answered ? "white" : "gray.500"}>
            {answered ? "✓" : index}
          </Text>
        </Flex>
        <Text fontSize="13.5px" fontWeight="700" color="gray.800">
          {question.label}
        </Text>
      </Flex>
      {children}
    </Box>
  );
}

// ─── Plain Radio options ──────────────────────────────────────────────────────
function RadioQ({ question, value, onChange }) {
  return (
    <Flex gap={2} flexWrap="wrap">
      {question.options.map((opt) => {
        const isSel = value === opt.id;
        return (
          <Box
            key={opt.id}
            border={isSel ? "2px solid" : "1px solid"}
            borderColor={isSel ? "cyan.500" : "gray.200"}
            borderRadius="8px" px={3} py={2}
            cursor="pointer" bg={isSel ? "cyan.50" : "white"}
            transition="all 0.15s" _hover={{ borderColor: "cyan.300" }}
            onClick={() => onChange(opt.id)}
          >
            <Flex align="center" gap={2}>
              <Box
                w="14px" h="14px" borderRadius="full" flexShrink={0}
                border={isSel ? "4px solid" : "1.5px solid"}
                borderColor={isSel ? "cyan.500" : "gray.300"}
                bg="white" transition="all 0.15s"
              />
              <Text fontSize="13px" fontWeight={isSel ? "600" : "400"} color={isSel ? "cyan.700" : "gray.700"}>
                {opt.label}
              </Text>
            </Flex>
          </Box>
        );
      })}
    </Flex>
  );
}

// ─── Image grid ───────────────────────────────────────────────────────────────
function ImageSelectQ({ question, value, onChange }) {
  const [zoomSrc, setZoomSrc] = useState(null);

  return (
    <>
      <SimpleGrid columns={2} spacing="10px">
        {question.options.map((opt) => {
          const isSel = value === opt.id;
          return (
            <Box
              key={opt.id}
              cursor="pointer"
              border={isSel ? "2px solid" : "1px solid"}
              borderColor={isSel ? "cyan.500" : "gray.200"}
              borderRadius="12px"
              overflow="hidden"
              bg="white"
              boxShadow={isSel ? "0 0 0 3px #E6FFFA" : "0 1px 2px rgba(0,0,0,0.05)"}
              transition="all 0.15s"
              _active={{ transform: "scale(0.97)" }}
              onClick={() => onChange(opt.id)}
            >
              {opt.img ? (
                <Box h="92px" position="relative" bg="gray.50">
                  <Box as="img" src={opt.img} alt={opt.label} w="100%" h="100%" objectFit="cover" />

                  {/* zoom-in button, top-left — view this option bigger */}
                  <IconButton
                    aria-label="View bigger"
                    icon={<Icon as={FiMaximize2} boxSize="11px" />}
                    size="xs"
                    minW="22px" h="22px"
                    position="absolute" top="5px" left="5px"
                    bg="blackAlpha.600" color="white"
                    borderRadius="full"
                    _hover={{ bg: "blackAlpha.700" }}
                    onClick={(e) => { e.stopPropagation(); setZoomSrc(opt.img); }}
                  />

                  {isSel && (
                    <Flex
                      position="absolute" top="6px" right="6px"
                      boxSize="22px" borderRadius="full" bg="cyan.500"
                      align="center" justify="center"
                      boxShadow="0 2px 6px rgba(0,0,0,0.3)"
                    >
                      <Text fontSize="12px" color="white" fontWeight="800">✓</Text>
                    </Flex>
                  )}
                </Box>
              ) : (
                <Flex h="92px" bg="orange.50" align="center" justify="center">
                  <Text fontSize="28px">✏️</Text>
                </Flex>
              )}
              <Box px={2.5} py={2} bg={isSel ? "cyan.50" : "white"}>
                <Text
                  fontSize="12.5px" textAlign="center" lineHeight="1.3"
                  color={isSel ? "cyan.700" : "gray.700"}
                  fontWeight={isSel ? "700" : "600"}
                >
                  {opt.label}
                </Text>
              </Box>
            </Box>
          );
        })}
      </SimpleGrid>

      {zoomSrc && (
        <ZoomOverlay src={zoomSrc} alt={question.label} onClose={() => setZoomSrc(null)} />
      )}
    </>
  );
}

// ─── Swipe card ───────────────────────────────────────────────────────────────
function SwipeCard({ opt, onLike, onDislike, current, total }) {
  const startX = useRef(null);
  const [drag, setDrag]   = useState(0);
  const [going, setGoing] = useState(null);
  const [zoomOpen, setZoomOpen] = useState(false);
  const THRESHOLD = 60;
  const getX = (e) => (e.touches ? e.touches[0]?.clientX : e.clientX);
  const onStart = (e) => { startX.current = getX(e); };
  const onMove  = (e) => { if (startX.current === null) return; setDrag(getX(e) - startX.current); };
  const onEnd   = () => {
    if (drag > THRESHOLD)       { setGoing("right"); setTimeout(() => { setDrag(0); setGoing(null); onLike(); }, 240); }
    else if (drag < -THRESHOLD) { setGoing("left");  setTimeout(() => { setDrag(0); setGoing(null); onDislike(); }, 240); }
    else setDrag(0);
    startX.current = null;
  };
  const tx  = going === "left" ? -380 : going === "right" ? 380 : drag;
  const rot = Math.min(Math.max(drag / 14, -12), 12);

  return (
    <Box
      borderRadius="10px" overflow="hidden"
      border="1px solid" borderColor="gray.200" boxShadow="sm"
      cursor="grab" userSelect="none"
      style={{ transform: `translateX(${tx}px) rotate(${rot}deg)`, opacity: going ? 0 : 1, transition: going ? "all 0.24s ease" : "transform 0.05s linear" }}
      onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
      onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
    >
      {opt.img ? (
        <Box h="130px" bgImage={`url(${opt.img})`} bgSize="cover" bgPos="center" position="relative">
          {/* zoom-in button, top-left (nudged down so it doesn't collide
              with the "Skip" label that appears in the same corner while
              actively dragging) */}
          <IconButton
            aria-label="View bigger"
            icon={<Icon as={FiMaximize2} boxSize="12px" />}
            size="xs"
            position="absolute" top="30px" left={2}
            bg="blackAlpha.600" color="white"
            borderRadius="full"
            _hover={{ bg: "blackAlpha.700" }}
            onClick={(e) => { e.stopPropagation(); setZoomOpen(true); }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          />
          {zoomOpen && (
            <ZoomOverlay src={opt.img} alt={opt.label} onClose={() => setZoomOpen(false)} />
          )}
          {drag < -20 && <Box position="absolute" top={2} left={2} bg="red.500" px={2} py={0.5} borderRadius="5px"><Text fontSize="10px" fontWeight="700" color="white">✕ Skip</Text></Box>}
          {drag > 20  && <Box position="absolute" top={2} right={2} bg="cyan.500" px={2} py={0.5} borderRadius="5px"><Text fontSize="10px" fontWeight="700" color="white">✓ Like</Text></Box>}
          <Box position="absolute" top={2} right={2} bg="blackAlpha.600" px={1.5} py={0.5} borderRadius="20px">
            <Text fontSize="9px" color="white" fontWeight="600">{current}/{total}</Text>
          </Box>
          <Flex position="absolute" bottom={0} left={0} right={0} justify="space-between" align="center"
            px={3} py={1.5} bgGradient="linear(to-t, blackAlpha.700, transparent)">
            <Flex align="center" gap={1}>
              <Box w="15px" h="15px" borderRadius="full" bg="red.400" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                <Text fontSize="8px" color="white" fontWeight="800">✕</Text>
              </Box>
              <Text fontSize="8px" color="white">Don't like</Text>
            </Flex>
            <Flex align="center" gap={1}>
              <Text fontSize="8px" color="white">Like</Text>
              <Box w="15px" h="15px" borderRadius="full" bg="cyan.400" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                <Text fontSize="8px" color="white" fontWeight="800">✓</Text>
              </Box>
            </Flex>
          </Flex>
        </Box>
      ) : (
        <Flex h="70px" bg="gray.50" align="center" justify="center" direction="column" gap={1}>
          <Text fontSize="18px">✏️</Text>
          <Text fontSize="11px" color="gray.500">Designer Choice</Text>
        </Flex>
      )}
      <Box bg="white" px={2} py={1.5}>
        <Text fontSize="12px" fontWeight="600" color="gray.800">{opt.label}</Text>
      </Box>
    </Box>
  );
}

function RadioImageQ({ question, value, onChange }) {
  const withImg = question.options.filter((o) => o.img);
  const [ci, setCi]       = useState(0);
  const [liked, setLiked] = useState([]);
  const allDone = ci >= withImg.length;
  const current = withImg[ci];

  const handleLike    = () => { const id = withImg[ci]?.id; if (!id) return; setLiked((l) => [...l, id]); onChange(id); setCi((i) => i + 1); };
  const handleDislike = () => setCi((i) => i + 1);

  return (
    <VStack spacing={2} align="stretch">
      {!allDone ? (
        <>
          <SwipeCard key={ci} opt={current} onLike={handleLike} onDislike={handleDislike} current={ci + 1} total={withImg.length} />
          <Flex gap={2} justify="center" align="center">
            <IconButton onClick={handleDislike} aria-label="skip" borderRadius="full" w="36px" h="36px"
              bg="red.50" color="red.500" border="1px solid" borderColor="red.200" _hover={{ bg: "red.100" }}
              icon={<Text fontSize="14px" lineHeight="1">✕</Text>} />
            <Text fontSize="10px" color="gray.400" fontWeight="500">{ci + 1}/{withImg.length}</Text>
            <IconButton onClick={handleLike} aria-label="like" borderRadius="full" w="36px" h="36px"
              bg="cyan.50" color="cyan.600" border="1px solid" borderColor="cyan.300" _hover={{ bg: "cyan.100" }}
              icon={<Text fontSize="14px" lineHeight="1">✓</Text>} />
          </Flex>
        </>
      ) : (
        <Box bg="cyan.50" border="1px solid" borderColor="cyan.200" borderRadius="8px" px={2} py={1.5} textAlign="center">
          <Text fontSize="11px" color="cyan.700" fontWeight="600">All reviewed tap thumbnail to select</Text>
        </Box>
      )}
      <Flex gap="5px" flexWrap="wrap">
        {question.options.map((opt) => {
          const isSel   = value === opt.id;
          const isLiked = liked.includes(opt.id);
          return (
            <Box key={opt.id} position="relative" w="48px" h="38px" borderRadius="6px" overflow="hidden"
              border={isSel ? "2px solid" : "1.5px solid"}
              borderColor={isSel ? "cyan.500" : isLiked ? "cyan.300" : "gray.200"}
              cursor="pointer" flexShrink={0} onClick={() => onChange(opt.id)}
              transition="all 0.15s" _hover={{ borderColor: "cyan.300" }}>
              {opt.img
                ? <Box w="100%" h="100%" bgImage={`url(${opt.img})`} bgSize="cover" bgPos="center" />
                : <Flex w="100%" h="100%" bg="gray.50" align="center" justify="center"><Text fontSize="12px">✏️</Text></Flex>
              }
              {isSel && (
                <>
                  <Box position="absolute" inset={0} bg="cyan.500" opacity={0.38} />
                  <Flex position="absolute" inset={0} align="center" justify="center">
                    <Text fontSize="11px" color="white" fontWeight="800">✓</Text>
                  </Flex>
                </>
              )}
            </Box>
          );
        })}
      </Flex>
      {value && (
        <Box bg="cyan.50" border="1px solid" borderColor="cyan.200" borderRadius="7px" px={2} py={1}>
          <Text fontSize="11px" color="cyan.700" fontWeight="600">
            ✓ {question.options.find((o) => o.id === value)?.label}
          </Text>
        </Box>
      )}
    </VStack>
  );
}

// ─── Room Form inside popup ───────────────────────────────────────────────────
function RoomForm({ roomId, roomLabel, roomAnswers, onUpdate, onClose }) {
  const questions     = ROOM_QUESTIONS[roomId] || [];
  const [localAns, setLocalAns] = useState(roomAnswers || {});
  const answeredCount = Object.values(localAns).filter(Boolean).length;
  const pct           = questions.length ? Math.round((answeredCount / questions.length) * 100) : 0;

  const handleAnswer = (qId, val) => setLocalAns((a) => ({ ...a, [qId]: val }));
  const handleSave   = () => { onUpdate(localAns); onClose(); };

  return (
    <Box>
      <Flex align="center" justify="space-between" mb={2}>
        <Text fontSize="11px" color="gray.500">{answeredCount} / {questions.length} answered</Text>
        <Text fontSize="11px" color="cyan.600" fontWeight="600">{pct}%</Text>
      </Flex>
      <Box bg="gray.100" borderRadius="full" h="3px" mb={4}>
        <Box bg="cyan.500" borderRadius="full" h="3px" w={`${pct}%`} transition="width 0.3s" />
      </Box>
      <VStack spacing={3} align="stretch" pb={4}>
        {questions.map((q, idx) => {
          const val      = localAns[q.id] || "";
          const answered = !!val;
          return (
            <QuestionCard key={q.id} index={idx + 1} total={questions.length} question={q} answered={answered}>
              {q.display === "radio"        && <RadioQ       question={q} value={val} onChange={(v) => handleAnswer(q.id, v)} />}
              {q.display === "image_select" && <ImageSelectQ question={q} value={val} onChange={(v) => handleAnswer(q.id, v)} />}
              {q.display === "radio_image"  && <RadioImageQ  question={q} value={val} onChange={(v) => handleAnswer(q.id, v)} />}
            </QuestionCard>
          );
        })}
      </VStack>
      <Button onClick={handleSave} bg="cyan.500" color="white" w="100%" h="42px"
        borderRadius="8px" fontWeight="600" fontSize="14px" _hover={{ bg: "cyan.600" }}>
        Save {roomLabel} ✓
      </Button>
    </Box>
  );
}

// ─── Room chip with +/- counter ───────────────────────────────────────────────
// Shows room name; first click adds it, subsequent clicks show +/- to add more
function RoomChip({ room, count, onAdd, onRemove }) {
  const isSelected = count > 0;
  return (
    <Box
      border={isSelected ? "2px solid" : "1px solid"}
      borderColor={isSelected ? "cyan.500" : "gray.200"}
      borderRadius="full"
      bg={isSelected ? "cyan.50" : "white"}
      boxShadow="sm"
      transition="all 0.15s"
      overflow="hidden"
    >
      {!isSelected ? (
        // Not yet selected — single tap to add
        <Box
          px={3} py={1.5} cursor="pointer"
          _hover={{ borderColor: "cyan.400", bg: "cyan.50" }}
          onClick={onAdd}
        >
          <Flex align="center" gap={1.5}>
            <Text fontSize="13px" lineHeight="1">{room.icon}</Text>
            <Text fontSize="13px" fontWeight="400" color="gray.700" whiteSpace="nowrap">
              {room.label}
            </Text>
          </Flex>
        </Box>
      ) : (
        // Already selected — show counter with –  count  +
        <Flex align="center">
          {/* Room label */}
          <Box px={3} py={1.5}>
            <Flex align="center" gap={1.5}>
              <Text fontSize="13px" lineHeight="1">{room.icon}</Text>
              <Text fontSize="13px" fontWeight="600" color="cyan.700" whiteSpace="nowrap">
                {room.label}
              </Text>
            </Flex>
          </Box>

          {/* Counter pill */}
          <Flex
            align="center"
            bg="cyan.500"
            borderRadius="full"
            mr={1}
            px={1}
            py={0.5}
            gap={0}
          >
            {/* Minus */}
            <Box
              w="22px" h="22px" display="flex" alignItems="center" justifyContent="center"
              cursor="pointer" borderRadius="full"
              _hover={{ bg: "cyan.600" }}
              onClick={onRemove}
            >
              <Text fontSize="14px" color="white" fontWeight="700" lineHeight="1">−</Text>
            </Box>

            {/* Count badge */}
            <Box
              minW="20px" textAlign="center"
              px={1}
            >
              <Text fontSize="12px" color="white" fontWeight="700">{count}</Text>
            </Box>

            {/* Plus */}
            <Box
              w="22px" h="22px" display="flex" alignItems="center" justifyContent="center"
              cursor="pointer" borderRadius="full"
              _hover={{ bg: "cyan.600" }}
              onClick={onAdd}
            >                                                                                                       
              <Text fontSize="14px" color="white" fontWeight="700" lineHeight="1">+</Text>
            </Box>
          </Flex>
        </Flex>
      )}
    </Box>
  );
}

// ─── Main Step 3 ──────────────────────────────────────────────────────────────
export default function InteriorStep3UnitDescription({
  formData, setFormData, onNext, onBack, isLastStep, onSubmit, viewMode,
}) {
  const numberOfFloors = Math.min(parseInt(formData?.interiorFloors || "1") || 1, 6);
  const floorRooms     = formData?.floorRooms      || {};
  const roomData       = formData?.interiorRoomData || {};

  const FLOOR_NAMES = ["Ground Floor", "First Floor", "Second Floor", "Third Floor", "Fourth Floor", "Fifth Floor"];
  const floorLabels = Array.from({ length: numberOfFloors }, (_, i) => FLOOR_NAMES[i] || `Floor ${i + 1}`);

  const [activeFloor, setActiveFloor] = useState(floorLabels[0]);
  const [activeRoom,  setActiveRoom]  = useState(null);
  const { isOpen, onOpen, onClose }   = useDisclosure();

  // ── Add one more instance of roomId on activeFloor
  const addRoom = (floorLabel, roomId) => {
    const current    = floorRooms[floorLabel] || [];
    const instanceId = nextInstanceId(current, roomId);
    setFormData({
      ...formData,
      floorRooms: { ...floorRooms, [floorLabel]: [...current, instanceId] },
    });
  };

  // ── Remove the LAST instance of roomId from activeFloor (and its answers)
  const removeRoom = (floorLabel, roomId) => {
    const current  = floorRooms[floorLabel] || [];
    // Find the last instance id of this roomId
    const instances = current.filter((id) => baseId(id) === roomId);
    if (!instances.length) return;
    const lastInstance = instances[instances.length - 1];

    const updatedRooms   = current.filter((id) => id !== lastInstance);
    const dataKey        = `${floorLabel}_${lastInstance}`;
    const updatedRoomData = { ...roomData };
    delete updatedRoomData[dataKey];

    setFormData({
      ...formData,
      floorRooms:       { ...floorRooms, [floorLabel]: updatedRooms },
      interiorRoomData: updatedRoomData,
    });
  };

  const openRoomForm = (floorLabel, instanceId, label) => {
    setActiveRoom({ floorLabel, instanceId, roomId: baseId(instanceId), roomLabel: label });
    onOpen();
  };

  const saveRoomAnswers = (answers) => {
    const key = `${activeRoom.floorLabel}_${activeRoom.instanceId}`;
    setFormData({ ...formData, interiorRoomData: { ...roomData, [key]: answers } });
  };

  const getAnswerCount = (floorLabel, instanceId) => {
    const ans = roomData[`${floorLabel}_${instanceId}`] || {};
    return Object.values(ans).filter(Boolean).length;
  };

  const totalSelected = Object.values(floorRooms).flat().length;
  const canProceed    = totalSelected > 0;

  const handleNext = () => {
    if (!canProceed) return;
    if (isLastStep) onSubmit(); else onNext();
  };

  return (
    <Box h="600px" w="100%" maxW="400px" mx="auto"
      display="flex" flexDirection="column" justifyContent="space-between" px={4} py={6}>
      <Box flex="1" minH="0" overflowY="auto" overflowX="hidden" pr={1}
        pointerEvents={viewMode ? "none" : "auto"}
        opacity={viewMode ? 0.6 : 1}
        css={{ "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "10px" } }}>
        <VStack spacing={4} align="stretch">

          <Box>
            <Text fontSize="17px" fontWeight="600" color="gray.800">Unit Description</Text>
            <Text fontSize="12px" color="gray.500" mt={0.5}>
              Select rooms per floor — tap <Text as="span" color="cyan.600" fontWeight="600">+</Text> to add multiple of the same room
            </Text>
          </Box>

          {/* Floor tabs */}
          {floorLabels.length > 1 && (
            <Flex gap={2} flexWrap="wrap">
              {floorLabels.map((fl) => {
                const count    = (floorRooms[fl] || []).length;
                const isActive = activeFloor === fl;
                return (
                  <Box key={fl} px={3} py={1.5} borderRadius="8px" cursor="pointer"
                    bg={isActive ? "cyan.500" : "gray.100"}
                    color={isActive ? "white" : "gray.600"}
                    fontSize="13px" fontWeight="500" transition="all 0.15s"
                    onClick={() => setActiveFloor(fl)}>
                    {fl}
                    {count > 0 && (
                      <Badge ml={1} fontSize="9px" borderRadius="full"
                        bg={isActive ? "white" : "cyan.100"}
                        color={isActive ? "cyan.600" : "cyan.700"}>
                        {count}
                      </Badge>
                    )}
                  </Box>
                );
              })}
            </Flex>
          )}

          {/* Room chips with +/- counter */}
          <Box>
            <Flex align="center" justify="space-between" mb={2}>
              <Text fontSize="13px" fontWeight="600" color="gray.600">
                {activeFloor} — Select Rooms
              </Text>
              {(floorRooms[activeFloor] || []).length > 0 && (
                <Text fontSize="11px" color="cyan.600" fontWeight="500">
                  {(floorRooms[activeFloor] || []).length} room{(floorRooms[activeFloor] || []).length > 1 ? "s" : ""} selected
                </Text>
              )}
            </Flex>
            <Flex gap={2} flexWrap="wrap">
              {ROOM_TYPES.map((room) => {
                const count = countInstances(floorRooms[activeFloor] || [], room.id);
                return (
                  <RoomChip
                    key={room.id}
                    room={room}
                    count={count}
                    onAdd={() => addRoom(activeFloor, room.id)}
                    onRemove={() => removeRoom(activeFloor, room.id)}
                  />
                );
              })}
            </Flex>
          </Box>

          {/* Selected room instance cards */}
          {(floorRooms[activeFloor] || []).length > 0 && (
            <Box>
              <Text fontSize="13px" fontWeight="600" color="gray.600" mb={2}>Fill Room Details</Text>
              <VStack spacing={2} align="stretch">
                {(floorRooms[activeFloor] || []).map((instanceId) => {
                  const rid      = baseId(instanceId);
                  const room     = ROOM_TYPES.find((r) => r.id === rid);
                  const label    = instanceLabel(instanceId, room?.label || rid);
                  const ansCount = getAnswerCount(activeFloor, instanceId);
                  const totalQ   = (ROOM_QUESTIONS[rid] || []).length;
                  const pct      = totalQ ? Math.round((ansCount / totalQ) * 100) : 0;

                  return (
                    <Box key={instanceId}
                      border="1px solid"
                      borderColor={pct === 100 ? "cyan.300" : "gray.200"}
                      borderRadius="10px" px={3} py={2}
                      bg={pct === 100 ? "cyan.50" : "white"}
                      cursor="pointer" transition="all 0.15s"
                      _hover={{ borderColor: "cyan.300", bg: "cyan.50" }}
                      onClick={() => openRoomForm(activeFloor, instanceId, label)}
                    >
                      <Flex align="center" justify="space-between">
                        <Flex align="center" gap={2}>
                          <Text fontSize="18px">{room?.icon}</Text>
                          <Box>
                            <Flex align="center" gap={1.5}>
                              <Text fontSize="13px" fontWeight="600" color="gray.800">{label}</Text>
                              {/* Show instance number badge for duplicates */}
                              {instanceId.includes("_") && (
                                <Badge colorScheme="purple" fontSize="9px" variant="subtle" borderRadius="4px">
                                  #{instanceId.match(/_(\d+)$/)?.[1]}
                                </Badge>
                              )}
                            </Flex>
                            <Text fontSize="10px" color="gray.400">{ansCount}/{totalQ} answered</Text>
                          </Box>
                        </Flex>
                        <Badge
                          colorScheme={pct === 100 ? "green" : pct > 0 ? "yellow" : "gray"}
                          fontSize="9px" px={2} borderRadius="5px">
                          {pct === 100 ? "✓ Done" : pct > 0 ? `${pct}%` : "Fill →"}
                        </Badge>
                      </Flex>
                      <Box bg="gray.100" borderRadius="full" h="2px" mt={2}>
                        <Box bg="cyan.400" borderRadius="full" h="2px"
                          w={`${pct}%`} transition="width 0.3s" />
                      </Box>
                    </Box>
                  );
                })}
              </VStack>
            </Box>
          )}
        </VStack>
      </Box>

      {/* Room detail popup */}
      <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="full">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent maxW="400px" mx="auto" my={0}
          borderRadius={{ base: "0", md: "16px" }}
          maxH="100vh" display="flex" flexDirection="column">
          <Box px={4} pt={4} pb={3} borderBottom="1px solid" borderColor="gray.100" flexShrink={0}>
            <Flex align="center" justify="space-between" mb={1}>
              <Flex align="center" gap={2}>
                <Text fontSize="15px" fontWeight="700" color="gray.800">
                  {activeRoom?.roomLabel}
                </Text>
                {activeRoom?.instanceId?.includes("_") && (
                  <Badge colorScheme="purple" fontSize="10px" variant="subtle">
                    Room #{activeRoom.instanceId.match(/_(\d+)$/)?.[1]}
                  </Badge>
                )}
              </Flex>
              <Flex align="center" gap={2}>
                <Badge colorScheme="cyan" fontSize="10px" variant="subtle">
                  {activeRoom?.floorLabel}
                </Badge>
                <ModalCloseButton position="static" size="sm" />
              </Flex>
            </Flex>
            <Text fontSize="11px" color="gray.400">Tap any card to select your preference</Text>
          </Box>

          <ModalBody px={4} py={4} overflowY="auto">
            {activeRoom && (
              <RoomForm
                key={`${activeRoom.floorLabel}_${activeRoom.instanceId}`}
                roomId={activeRoom.roomId}
                roomLabel={activeRoom.roomLabel}
                roomAnswers={roomData[`${activeRoom.floorLabel}_${activeRoom.instanceId}`] || {}}
                onUpdate={saveRoomAnswers}
                onClose={onClose}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button onClick={onBack} variant="solid" bg="white" color="cyan.600"
          border="1px solid" borderColor="cyan.500" borderRadius="6px"
          minW="100px" h="40px" fontWeight="500" fontSize="14px" _hover={{ bg: "cyan.50" }}>
          ← Previous
        </Button>
        <Button onClick={handleNext} isDisabled={!canProceed} variant="solid"
          bg={canProceed ? "cyan.500" : "gray.300"} color="white"
          borderRadius="6px" minW="100px" h="40px" fontWeight="500" fontSize="14px" ml="auto"
          _hover={{ bg: canProceed ? "cyan.600" : "gray.300" }}
          _disabled={{ bg: "gray.300", color: "gray.500", cursor: "not-allowed" }}>
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}